import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Loader2, X } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { ICON_CHOICES, resolveIcon } from "../lib/liveStatusIcons";
import { BRAND_COLORS, withAlpha, STATUS_COLORS } from "../lib/bookingStatus";

/**
 * AddTileModal — Feb 2026 · schnelles Formular, um dem systemweiten
 * Live-Status-Schema eine neue Kachel hinzuzufügen, ohne die Admin-Seite
 * `/team/live-status-schema` öffnen zu müssen. Aufrufbar direkt aus dem
 * Team-Kunden-Detail (Bereich „Objekte · Live-Status").
 *
 * Props:
 *   • existingSchema — aktuelles Schema (Array) für Kollisions-Check
 *   • onClose        — Modal schließen
 *   • onSaved(newSchemaItems) — Callback mit dem neuen, gespeicherten Schema
 *
 * Die neue Zeile wird IMMER als letzter Eintrag angehängt und ist
 * `locked=false` (Custom-Item). Der Admin kann sie später auf der
 * Live-Status-Struktur-Seite feiner sortieren, umbenennen oder löschen.
 */

const NAVY = BRAND_COLORS.navy;
const GOLD = BRAND_COLORS.gold;
const IVORY = BRAND_COLORS.ivory;

export const CATEGORY_CHOICES = [
  { value: "technik", label: "Technik" },
  { value: "sicherheit", label: "Sicherheit" },
  { value: "aussenbereich", label: "Außenbereich" },
  { value: "innenbereich", label: "Innenbereich" },
  { value: "verwaltung", label: "Verwaltung" },
  { value: "sonstiges", label: "Sonstiges" },
];

export function categoryLabel(value) {
  return (CATEGORY_CHOICES.find((c) => c.value === value) || {}).label || "Sonstiges";
}

export default function AddTileModal({ existingSchema = [], onClose, onSaved }) {
  const [label, setLabel] = useState("");
  const [icon, setIcon] = useState("Circle");
  const [category, setCategory] = useState("technik");
  const [inputType, setInputType] = useState("text");
  const [saving, setSaving] = useState(false);
  const Icon = resolveIcon(icon);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const suggestedKey = label
    .trim()
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);

  const collides = existingSchema.some((it) => it.key === suggestedKey);
  const canSave = label.trim().length >= 2 && suggestedKey.length >= 2 && !collides && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // Bestehendes Schema + neue Zeile am Ende. Alle Locked/Custom Items
      // bleiben erhalten — der Server erzwingt die Locked-Reihenfolge nicht,
      // nur ihre Anwesenheit.
      const items = [
        ...existingSchema.map((it) => ({
          key: it.key,
          label: it.label,
          icon: it.icon,
          input_type: it.input_type,
          locked: !!it.locked,
          category: it.category || "sonstiges",
          placeholder: it.placeholder || null,
          hint: it.hint || null,
        })),
        {
          key: suggestedKey,
          label: label.trim(),
          icon: icon || "Circle",
          input_type: inputType,
          locked: false,
          category,
          placeholder: null,
          hint: null,
        },
      ];
      const { data } = await api.put("/team/live-status-schema", { items });
      toast.success(`Kachel „${label.trim()}" wurde systemweit angelegt.`);
      onSaved?.(data.items || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6"
      style={{ background: withAlpha(BRAND_COLORS.navy, 0.55) }}
      onClick={onClose}
      data-testid="add-tile-modal"
    >
      <div
        className="bg-ivory w-full md:max-w-[520px] shadow-2xl relative animate-fade-in"
        style={{ borderRadius: "12px 12px 0 0", maxHeight: "88vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-ivory/95 backdrop-blur px-5 py-4 border-b border-navy/10 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="label-caps" style={{ color: GOLD }}>Neue Kachel</div>
            <div className="font-display text-lg text-navy leading-tight mt-0.5">
              Kachel hinzufügen
            </div>
            <div className="text-[11.5px] text-navy-dark/60 italic mt-0.5">
              Die neue Zeile erscheint sofort im Team-Editor und kann pro Kunde als Kachel aktiviert werden.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="add-tile-close"
            aria-label="Schließen"
            className="shrink-0 w-8 h-8 flex items-center justify-center border border-navy/15 hover:border-navy text-navy-dark transition-colors bg-white"
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); save(); }}
          className="p-5 space-y-4"
        >
          {/* Name */}
          <div>
            <label className="label-caps mb-1.5 block" htmlFor="new-tile-label">Name der Kachel</label>
            <input
              id="new-tile-label"
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="z. B. Pool-Status"
              maxLength={80}
              disabled={saving}
              data-testid="add-tile-label-input"
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[14px] text-navy"
            />
            <div className="text-[10.5px] text-navy-dark/50 mt-1">
              Schlüssel wird automatisch aus dem Namen erzeugt:{" "}
              <code className="font-mono">{suggestedKey || "—"}</code>
              {collides && (
                <span className="ml-2 text-[10.5px]" style={{ color: STATUS_COLORS.red }}>
                  · bereits vorhanden
                </span>
              )}
            </div>
          </div>

          {/* Icon */}
          <div>
            <label className="label-caps mb-1.5 block" htmlFor="new-tile-icon">Icon</label>
            <div className="flex items-center gap-3">
              <span
                className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
                style={{ background: IVORY }}
                data-testid="add-tile-icon-preview"
              >
                <Icon className="w-5 h-5" strokeWidth={1.5} style={{ color: NAVY }} />
              </span>
              <select
                id="new-tile-icon"
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                disabled={saving}
                data-testid="add-tile-icon-select"
                className="flex-1 bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[13px] text-navy"
              >
                {ICON_CHOICES.map((ic) => (
                  <option key={ic.value} value={ic.value}>{ic.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="label-caps mb-1.5 block" htmlFor="new-tile-category">Kategorie</label>
            <select
              id="new-tile-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={saving}
              data-testid="add-tile-category-select"
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[13px] text-navy"
            >
              {CATEGORY_CHOICES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <div className="text-[10.5px] text-navy-dark/50 mt-1">
              Kategorie hilft dem Team, ähnliche Punkte im Editor zu gruppieren.
            </div>
          </div>

          {/* Input type */}
          <div>
            <label className="label-caps mb-1.5 block" htmlFor="new-tile-input-type">Eingabetyp</label>
            <select
              id="new-tile-input-type"
              value={inputType}
              onChange={(e) => setInputType(e.target.value)}
              disabled={saving}
              data-testid="add-tile-input-type-select"
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2.5 text-[13px] text-navy"
            >
              <option value="text">Text — freier Text (z. B. „Sauber")</option>
              <option value="number">Zahl — z. B. 21</option>
              <option value="date">Datum — YYYY-MM-DD</option>
            </select>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="text-[11px] tracking-widest uppercase px-4 py-2.5 text-navy hover:text-gold-dark"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!canSave}
              data-testid="add-tile-save"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-40"
              style={{ background: NAVY }}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />}
              Kachel anlegen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
